// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import React from "react";
import { useContext } from "react";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import translate from "@insite/client-framework/Translate";
import { css } from "styled-components";
import { useCurrentPage } from "@toolkit/Framework/Store/Data/Pages/PageHooks";
import {
    BillToModel,
    CartModel,
    OrderModel,
    ShipToModel,
    WarehouseModel,
} from "@insite/client-framework/Types/ApiModels";
import { Cart } from "@insite/client-framework/Services/CartService";
import AddressInfoDisplayWithHeader from "@store/Library/Components/Common/AddressInfoDisplayWithHeader";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";

import {
    horizontalStyles,
    cartFulfillmentMethodStyles as verticalStyles,
} from "@store/Overrides/Widgets/Cart/CartFulfillmentMethodSelector";
import useIsMobile from "@store/Framework/Common/Hooks/useIsMobile";
import useCart from "@toolkit/Framework/Common/GoogleAnalytics/hooks/utilities/useCart";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";

type Props = WidgetProps;

export interface AddressInfo {
    companyName: string;
    address1: string;
    address2?: string;
    city: string;
    state: string;
    postalCode: string;
    phone?: string;
}

const billingWrapperStyles: InjectableCss = {
    css: css`
        display: flex;
        width: 100%;
        justify-content: center;
    `,
};

const getAddressInfo = (data: ShipToModel | BillToModel): AddressInfo => {
    return {
        address1: data.address1 ?? "",
        address2: data.address2,
        city: data.city ?? "",
        state: data.state?.abbreviation ?? "",
        postalCode: data.postalCode ?? "",
        phone: data.phone,
        companyName: data.companyName ?? "",
    };
};
const getAddressData = (
    isOrderHistoryPage: boolean,
    cart: Readonly<Cart> | undefined,
    order: Readonly<OrderModel> | undefined,
    cartBillTo: BillToModel,
    cartShipTo: ShipToModel,
): { billing: AddressInfo; shipping: AddressInfo; pickup?: AddressInfo } => {
    cart?.defaultWarehouse;
    if (isOrderHistoryPage) {
        const orderShippingInfo: ShipToModel | {} = JSON.parse(order?.properties?.shipTo ?? "{}");
        const orderPickupInfo: Partial<WarehouseModel> | {} = JSON.parse(order?.properties?.pickupLocation ?? "{}");
        const billing = {
            companyName: order?.btCompanyName ?? "",
            address1: order?.btAddress1 ?? "",
            address2: order?.btAddress2 ?? "",
            city: order?.billToCity ?? "",
            state: order?.billToState ?? "",
            postalCode: order?.billToPostalCode ?? "",
        };

        const defaultShipping = {
            companyName: order?.stCompanyName ?? "",
            address1: order?.stAddress1 ?? "",
            address2: order?.stAddress2 ?? "",
            city: order?.shipToCity ?? "",
            state: order?.shipToState ?? "",
            postalCode: order?.shipToPostalCode ?? "",
        };

        const shipping = "address1" in orderShippingInfo ? getAddressInfo(orderShippingInfo) : defaultShipping;

        const pickup =
            "description" in orderPickupInfo
                ? {
                      address1: orderPickupInfo.address1 ?? "",
                      address2: orderPickupInfo.address2,
                      city: orderPickupInfo?.city ?? "",
                      state: orderPickupInfo?.state ?? "",
                      postalCode: orderPickupInfo.postalCode ?? "",
                      phone: orderPickupInfo.phone,
                      companyName: orderPickupInfo.description ?? "",
                  }
                : shipping;

        return {
            billing,
            shipping,
            pickup: order?.fulfillmentMethod === FulfillmentMethod.PickUp ? pickup : undefined,
        };
    }

    const billing = getAddressInfo(cartBillTo);
    const shipping = getAddressInfo(cartShipTo);

    const pickup = {
        address1: cart?.defaultWarehouse?.address1 ?? "",
        address2: cart?.defaultWarehouse?.address2,
        city: cart?.defaultWarehouse?.city ?? "",
        state: cart?.defaultWarehouse?.state ?? "",
        postalCode: cart?.defaultWarehouse?.postalCode ?? "",
        phone: cart?.defaultWarehouse?.phone,
        companyName: cart?.defaultWarehouse?.description ?? "",
    };

    return { billing, shipping, pickup: cart?.fulfillmentMethod === FulfillmentMethod.PickUp ? pickup : undefined };
};

const getbillTo = (
    cart: Readonly<Cart> | CartModel | undefined,
    state: ApplicationState,
): Readonly<BillToModel> | undefined | null => {
    if (!cart) {
        return undefined;
    }
    if ("billToId" in cart) {
        return getBillToState(state, cart.billToId).value;
    }
    if ("billTo" in cart) {
        return cart.billTo;
    }
};

const getShipTo = (
    cart: Readonly<Cart> | CartModel | undefined,
    state: ApplicationState,
): Readonly<ShipToModel> | undefined | null => {
    if (!cart) {
        return undefined;
    }
    if ("shipToId" in cart) {
        return getShipToState(state, cart.shipToId).value;
    }
    if ("shipTo" in cart) {
        return cart.shipTo;
    }
};

const BillingShippingInfo: React.FC<Props> = () => {
    let cart = useCart();
    const isMobile = useIsMobile();
    const currentPage = useCurrentPage().type;

    const isOrderHistoryDetailsPage = currentPage === "OrderDetailsPage";
    const styles = isMobile ? verticalStyles : horizontalStyles;
    const orderContext = useContext(OrderStateContext)?.value;
    const cartBillTo = useAppSelector(state => getbillTo(cart, state));
    const cartShipTo = useAppSelector(state => getShipTo(cart, state));

    if (!isOrderHistoryDetailsPage && (!cart || !cartBillTo || !cartShipTo)) {
        return null;
    }

    // its safe to assume that cartBillTo and ShipTo are not null because we are checking if cart is null
    const { billing, shipping, pickup } = getAddressData(
        isOrderHistoryDetailsPage,
        cart,
        orderContext,
        cartBillTo!,
        cartShipTo!,
    );

    return (
        <StyledWrapper {...styles.fulfillmentMethodWrapper}>
            {billing && (
                <StyledWrapper {...billingWrapperStyles}>
                    <AddressInfoDisplayWithHeader addressInfo={billing} headline={translate("Billing Address")} />
                </StyledWrapper>
            )}

            {shipping && (
                <StyledWrapper {...styles.shippingAndPickUpDisplayWrapper}>
                    <AddressInfoDisplayWithHeader addressInfo={shipping} headline={translate("Shipping Address")} />
                </StyledWrapper>
            )}

            {pickup && (
                <StyledWrapper {...styles.shippingAndPickUpDisplayWrapper}>
                    <AddressInfoDisplayWithHeader addressInfo={pickup} headline={translate("Pick Up Location")} />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: BillingShippingInfo,
    definition: {
        displayName: "Billing Shipping Info",
        group: "Common",
    },
};

export default widgetModule;
